import React from "react";
import { connect } from "react-redux";
import { NavLink, withRouter } from "react-router-dom";
import { withTranslation } from "react-i18next";
import {
	FiMoreHorizontal,
	FiExternalLink,
	FiFileText,
	FiUser,
	FiInbox,
	FiFolder,
	FiArrowLeft,
	FiPlus,
	FiBriefcase,
	FiCalendar,
	FiStar,
	FiHeart,
	FiBookmark,
	FiHome,
	FiSettings,
	FiArchive,
	FiTag,
} from "react-icons/fi";
import { appService, authService, documentsService } from "services";
import Loading from "components/Loading";
import { showModal, hideModal } from "actions/modalActions";
import { toastr } from "react-redux-toastr";
import CreateFolderModal from "./CreateFolderModal";
import EmployeeDocumentsUploadModal from "../Employees/EmployeeDocumentsUploadModal";
import DocumentMoveModal from "./DocumentMoveModal";
import DocumentRenameModal from "./DocumentRenameModal";
import DocumentSignContent from "./DocumentSignContent";
import SignatureStatus from "./SignatureStatus";
import { isSignableDocument, getFormatErrorMessage } from "utils/documentUtils";
import { hasSubscriptionFeature } from "utils/utils";
import UpgradeModal from "components/Modal/UpgradeModal";
import {
	DocumentsNotificationsProvider,
	PendingSignatureCards,
	RecentDocumentsList,
	DOCUMENTS_PENDING_SIGNATURES_SLUG,
} from "./DocumentsNotifications";
import DocumentAttachmentActionsMenu, {
	resolveStoreFolderIdForAttachment,
	enrichAttachmentsWithStoreFolderIds,
	downloadStoreFolderAttachment,
} from "./DocumentAttachmentActionsMenu";
import { logAttachmentFolderFields, logFolderIdResolution } from "utils/documentFolderDebug";
import DataTable from "components/DataTable";

const iconMap = {
	"payslips-icon": FiInbox,
	"contracts-icon": FiFileText,
	"absences-icon": FiExternalLink,
	"personal-documents-icon": FiUser,
	"briefcase-icon": FiBriefcase,
	"calendar-icon": FiCalendar,
	"star-icon": FiStar,
	"heart-icon": FiHeart,
	"bookmark-icon": FiBookmark,
	"home-icon": FiHome,
	"settings-icon": FiSettings,
	"archive-icon": FiArchive,
	"tag-icon": FiTag,
	"folder-icon": FiFolder,
};

class MyDocuments extends React.Component {
	state = {
		folders: [],
		attachments: [],
		signaturitData: [],
		currentFolder: null,
		loading: true,
		error: null,
		openMenuId: null,
		sortField: 'name',
		sortDirection: 'asc',
		showUpgradeModal: false,
		upgradeFeature: '',
	}

	componentDidMount() {
		if (!this.props.category) {
			this.fetchFolders();
		} else if (this.isPendingSignaturesView()) {
			this.fetchPendingSignatures();
		} else {
			this.fetchAttachments(this.props.category);
		}
		document.addEventListener('click', this.handleClickOutside);
	}

	componentDidUpdate(prevProps) {
		if (this.props.category && this.props.category !== prevProps.category) {
			if (this.isPendingSignaturesView()) {
				this.fetchPendingSignatures();
			} else {
				this.fetchAttachments(this.props.category);
			}
		}

		if (this.props.folders && this.props.folders !== prevProps.folders) {
			this.setState({ folders: this.props.folders });
		}
	}

	componentWillUnmount() {
		document.removeEventListener('click', this.handleClickOutside);
	}

	handleClickOutside = (event) => {
		if (!event.target.closest('.category-header-actions')) {
			this.closeMenu();
		}
	};

	componentDidReceiveRefresh = () => {
		const category = this.props.category;

		if (this.isPendingSignaturesView()) {
			this.fetchPendingSignatures();
		} else if (category) {
			this.fetchAttachments(category);
		} else {
			this.fetchFolders(true);
		}
	}

	isPendingSignaturesView = () =>
		this.props.category === DOCUMENTS_PENDING_SIGNATURES_SLUG;

	getStoreFolderIdForAttachment = (attachment, debugContext) =>
		resolveStoreFolderIdForAttachment(
			attachment,
			this.state.currentFolder?.isPendingList ? undefined : this.state.currentFolder?.id,
			this.state.currentFolder?.isPendingList ? this.state.folders : undefined,
			debugContext ||
				(this.state.currentFolder?.isPendingList
					? "pending-list/getStoreFolderId"
					: "folder-view/getStoreFolderId")
		);

	getFolderDisplayName = (attachment) => {
		if (!attachment) return "—";
		if (attachment.folder_name) return attachment.folder_name;
		if (attachment.folderName) return attachment.folderName;
		if (attachment.store_folder?.name) return attachment.store_folder.name;
		if (attachment.storeFolder?.name) return attachment.storeFolder.name;
		return "—";
	};

	getSystemFolderName = (folder) => {
		if (!folder.system) return folder.name;
		const namesByIcon = {
			'payslips-icon': this.props.t('documents.systemFolders.payslips'),
			'contracts-icon': this.props.t('documents.systemFolders.contracts'),
			'absences-icon': this.props.t('documents.systemFolders.absences'),
			'personal-documents-icon': this.props.t('documents.systemFolders.personalDocuments'),
		}
		return namesByIcon[folder.icon] || folder.name;
	};

	fetchPendingSignatures = () => {
		this.setState({ loading: true, error: null });
		const employeeId = this.props.user?.id;

		logFolderIdResolution("pending-list/fetchPendingSignatures START", null, {
			employeeId,
			foldersInState: this.state.folders?.length ?? 0,
			foldersFromProps: this.props.folders?.length ?? 0,
			note:
				(this.state.folders?.length ?? 0) === 0
					? "sin carpetas en state: el fallback por foldersList tampoco resolverá folderId"
					: undefined,
		});

		documentsService
			.getPendingSignatures(employeeId)
			.then(async (response) => {
				if (!response.ok && !Array.isArray(response)) {
					throw new Error(this.props.t("documents.pendingSignatures"));
				}
				let list = [];
				if (Array.isArray(response)) {
					list = response;
				} else {
					list = response.data || [];
				}
				if (!Array.isArray(list)) {
					list = [];
				}

				logAttachmentFolderFields("GET /store-folders/signaturit/pending", list, {
					employeeId,
					rawResponseShape: Array.isArray(response) ? "array" : "object",
					rawResponseKeys: Array.isArray(response) ? [] : Object.keys(response || {}),
				});

				let folders = this.state.folders?.length
					? this.state.folders
					: this.props.folders?.length
						? this.props.folders
						: [];
				if (folders.length === 0) {
					try {
						const foldersResponse = await documentsService.getStoreFolders();
						folders = foldersResponse?.data || foldersResponse || [];
						if (!Array.isArray(folders)) {
							folders = [];
						}
					} catch (folderError) {
						console.error("Error fetching folders for pending signatures:", folderError);
					}
				}

				list = await enrichAttachmentsWithStoreFolderIds(list, folders, employeeId);

				logAttachmentFolderFields(
					"pending-list/enriched attachments",
					list,
					{ employeeId, foldersLoaded: folders.length }
				);

				const folderIds = [
					...new Set(
						list
							.map((a) => a.store_folder_id ?? a.folder_id ?? a.store_folder?.id)
							.filter((id) => id != null && id !== "")
					),
				];

				let signaturitData = [];
				if (folderIds.length > 0) {
					const sigResults = await Promise.all(
						folderIds.map((id) => documentsService.getSignaturitAttachments(id))
					);
					signaturitData = sigResults.flatMap((r) => {
						const d = r?.data ?? r ?? [];
						return Array.isArray(d) ? d : [];
					});
				}

				logFolderIdResolution("pending-list/fetchPendingSignatures", null, {
					extractedFolderIds: folderIds,
					signaturitDataCount: signaturitData.length,
					foldersInState: this.state.folders?.length ?? 0,
					note:
						folderIds.length === 0
							? "ningún adjunto trae store_folder_id → no se cargará signaturitData"
							: undefined,
				});

				const sortedAttachments = this.sortAttachments(list);

				this.setState({
					currentFolder: {
						id: DOCUMENTS_PENDING_SIGNATURES_SLUG,
						name: this.props.t("documents.pendingSignatures"),
						isPendingList: true,
					},
					folders,
					attachments: sortedAttachments,
					signaturitData,
					loading: false,
					error: null,
				});
			})
			.catch((error) => {
				console.error("Error fetching pending signatures:", error);
				this.setState({
					loading: false,
					error: error.message || this.props.t("documents.pendingSignatures"),
					attachments: [],
					signaturitData: [],
					currentFolder: {
						id: DOCUMENTS_PENDING_SIGNATURES_SLUG,
						name: this.props.t("documents.pendingSignatures"),
						isPendingList: true,
					},
				});
			});
	};

	fetchFolders = (forceRefresh = false) => {
		if (!forceRefresh && this.props.folders && this.props.folders.length > 0) {
			this.setState({ folders: this.props.folders, loading: false, error: null });
			return;
		}

		this.setState({ loading: true, error: null });

		documentsService.getStoreFolders()
			.then(response => {
				const folders = response.data || response || [];

				if (!folders || folders.length === 0) {
					this.setState({
						folders: [],
						loading: false,
						error: null
					});
					return;
				}

				this.setState({
					folders: folders,
					loading: false,
					error: null
				});
			})
			.catch(error => {
				console.error("❌ Error fetching folders:", error);

				let errorMessage = this.props.t('documents.folder.errorLoadingFolders');

				if (error.status === 404) {
					errorMessage = this.props.t('documents.folder.noFoldersFound');
				} else if (error.message) {
					errorMessage = error.message;
				}

				this.setState({
					error: errorMessage,
					loading: false,
					folders: []
				});
			});
	}

	fetchAttachments = (folderId) => {
		this.setState({ loading: true, error: null });

		// Hacer ambas llamadas en paralelo
		Promise.all([
			documentsService.getAttachments(folderId, null),
			documentsService.getSignaturitAttachments(folderId)
		])
			.then(([attachmentsResponse, signaturitResponse]) => {
				if (!attachmentsResponse.ok) {
					throw new Error(this.props.t('documents.errorApiResponse'));
				}

				const attachments = attachmentsResponse.data || attachmentsResponse || [];
				const signaturitData = signaturitResponse.data || signaturitResponse || [];

				// Ordenar los attachments en el frontend
				const sortedAttachments = this.sortAttachments(attachments);

				logAttachmentFolderFields("GET /store-folders/{id}/attachments", sortedAttachments, {
					folderId,
					source: "folder-view (funciona)",
				});

				this.setState(prevState => ({
					currentFolder: {
						...prevState.currentFolder,
						id: folderId,
						name: this.props.folderName || prevState.currentFolder?.name
					},
					attachments: sortedAttachments,
					signaturitData: signaturitData,
					loading: false,
					error: null
				}));

				if (!this.props.folderName && !this.state.currentFolder?.name) {
					this.fetchFolderName(folderId);
				}
			})
			.catch(error => {
				console.error("❌ Error fetching attachments:", error);

				let errorMessage = this.props.t('documents.noDocumentsInFolder');

				if (error.status === 404) {
					errorMessage = this.props.t('documents.noDocumentsInFolder');
				} else if (error.message) {
					errorMessage = error.message;
				}

				this.setState({
					error: errorMessage,
					loading: false,
					attachments: [],
					signaturitData: [],
					currentFolder: {
						id: folderId,
						name: null
					}
				});

				this.fetchFolderName(folderId);
			});
	}

	sortAttachments = (attachments) => {
		const { sortField, sortDirection } = this.state;

		return [...attachments].sort((a, b) => {
			let aValue, bValue;

			switch (sortField) {
				case 'name':
					aValue = a.name.toLowerCase();
					bValue = b.name.toLowerCase();
					break;
				case 'folder':
					aValue = (this.getFolderDisplayName(a) || '').toLowerCase();
					bValue = (this.getFolderDisplayName(b) || '').toLowerCase();
					break;
				case 'created':
					aValue = new Date(a.created);
					bValue = new Date(b.created);
					break;
				case 'size':
					aValue = a.size;
					bValue = b.size;
					break;
				default:
					aValue = a.name.toLowerCase();
					bValue = b.name.toLowerCase();
			}

			if (sortDirection === 'asc') {
				return aValue > bValue ? 1 : -1;
			} else {
				return aValue < bValue ? 1 : -1;
			}
		});
	}

	fetchFolderName = (folderId) => {
		documentsService.getStoreFolderById(folderId)
			.then((folderResponse) => {
				if (!folderResponse.ok) {
					throw new Error('Error al obtener el nombre del folder');
				}

				this.setState(prevState => ({
					currentFolder: {
						...prevState.currentFolder,
						name: folderResponse.name
					}
				}));
			})
			.catch(error => {
				console.error("Error fetching folder name:", error);
				this.setState(prevState => ({
					currentFolder: {
						...prevState.currentFolder,
						name: `Carpeta ${folderId}`
					}
				}));
			});
	}

	refreshFolders = () => {
		this.fetchFolders(true);

		if (this.props.onRefresh) {
			this.props.onRefresh();
		}
	}

	getCategories = () => {
		const { folders, loading, error } = this.state;
		const maxFolders = 8;
		const isAdmin = this.props.user && this.props.user.roles.includes("ADMIN");

		if (loading) {
			return <Loading />;
		}

		if (error) {
			return (
				<div className="alert alert-warning">
					<strong>{this.props.t("common.error")}:</strong> {error}
					<br />
					<small>{this.props.t("documents.refreshOrContact")}</small>
				</div>
			);
		}

		if (!folders || folders.length === 0) {
			return (
				<div className="alert alert-info">
					<strong>{this.props.t("common.error")}:</strong> {this.props.t("documents.folder.noFoldersAvailable")}
					<br />
					<small>{this.props.t("documents.folder.contactAdminFolders")}</small>
				</div>
			);
		}

		const systemFolderOrder = {
			'nóminas': 1,
			'contratos': 2,
			'ausencias': 3,
			'documentos personales': 4
		};

		const sortFolders = (folders) => {
			return folders.sort((a, b) => {
				if (a.system && b.system) {
					const orderA = systemFolderOrder[a.name.toLowerCase()] || 999;
					const orderB = systemFolderOrder[b.name.toLowerCase()] || 999;
					return orderA - orderB;
				}

				if (a.system && !b.system) return -1;
				if (!a.system && b.system) return 1;

				return a.name.localeCompare(b.name);
			});
		};

		const sortedFolders = sortFolders([...folders]);
		const systemFolders = sortedFolders.filter(folder => folder.system);
		const userFolders = sortedFolders.filter(folder => !folder.system);

		// Mostrar hasta 2 tarjetas de "Crear carpeta" a la vez, en función de las carpetas de usuario
		// Límite: hasta 8 carpetas creadas por el usuario (sin contar las del sistema)
		const maxUserFolders = 8;
		const ghostCardsCount = Math.max(0, Math.min(2, maxUserFolders - userFolders.length));

		return (
			<div className="row list-documents">
				{sortedFolders.map(folder => {
					const Icon = iconMap[folder.icon] || FiFolder;
					const hasDocuments = folder.total_attachments > 0;
					const isUserCreated = !folder.system;
					const isMenuOpen = this.state.openMenuId === folder.id;

					const folderContent = (
						<article className={`document-category ${!hasDocuments ? 'disabled' : ''}`}>
							{isUserCreated && isAdmin && (
								<div className="category-header-actions">
									<button
										className="category-actions-toggle"
										onClick={(e) => this.toggleMenu(folder.id, e)}
									>
										<FiMoreHorizontal />
									</button>
									{isMenuOpen && (
										<div className="custom-dropdown-menu">
											<button
												className="dropdown-item"
												onClick={() => this.handleMenuAction('edit', folder)}
											>
												{this.props.t("common.edit")}
											</button>
											<button
												className="dropdown-item"
												onClick={() => this.handleMenuAction('delete', folder)}
											>
												{this.props.t("common.delete")}
											</button>
										</div>
									)}
								</div>
							)}
							<Icon className="category-icon" />
							<h3 className="category-name">{this.getSystemFolderName(folder)}</h3>
							<div className="category-count">
								{folder.total_attachments} {this.props.t("documents.columns.document").toLowerCase()}{folder.total_attachments === 1 ? '' : 's'}
							</div>
						</article>
					);

					return (
						<div className="col-sm-6" key={folder.id}>
							{hasDocuments ? (
								<NavLink
									className="category-link"
									to={`/documents/${folder.id}`}
									exact
									onClick={(e) => {
										if (e.target.closest('.category-header-actions')) {
											e.preventDefault();
										}
									}}
								>
									{folderContent}
								</NavLink>
							) : (
								folderContent
							)}
						</div>
					)
				})}

				{/* Botones "Crear carpeta" */}
				{isAdmin && Array.from({ length: ghostCardsCount }, (_, index) => (
					<div className="col-sm-6" key={`create-folder-${index}`}>
						<article className="document-category ghost create-folder" onClick={this.openCreateFolderModal} style={{ cursor: 'pointer' }}>
							<FiPlus className="category-icon" />
							<h3 className="category-name">{this.props.t("documents.folder.create")}</h3>
							<div className="category-count">{this.props.t("documents.folder.createNew")}</div>
						</article>
					</div>
				))}
			</div>
		);
	};

	openCreateFolderModal = () => {
		this.props.showModal(
			{
				open: true,
				title: this.props.t("documents.folder.createNew"),
				style: { width: "500px" },
				content: (
					<CreateFolderModal
						handleClose={this.props.hideModal}
						onFolderCreated={this.handleFolderCreated}
					/>
				),
				closeModal: this.props.hideModal,
			},
			"edit"
		);
	};

	handleFolderCreated = () => {
		this.fetchFolders(true);

		if (this.props.onRefresh) {
			this.props.onRefresh();
		}
	};

	handleEditFolder = (folder) => {
		this.props.showModal(
			{
				open: true,
				title: this.props.t("documents.folder.edit"),
				style: { width: "500px" },
				content: (
					<CreateFolderModal
						handleClose={this.props.hideModal}
						onFolderCreated={this.handleFolderCreated}
						editMode={true}
						folderData={folder}
					/>
				),
				closeModal: this.props.hideModal,
			},
			"edit"
		);
	};

	handleDeleteFolder = (folder) => {
		this.props.showModal(
			{
				open: true,
				title: this.props.t('documents.folder.delete'),
				message: this.props.t('documents.folder.deleteConfirm', { name: this.getSystemFolderName(folder) }),
				deleteAction: () => {
					this.props.hideModal();
					this.deleteFolder(folder.id, this.getSystemFolderName(folder));
				},
				style: {
					width: '400px',
				},
				closeModal: this.props.hideModal
			},
			'delete'
		);
	};

	deleteFolder = (folderId, folderName) => {
		const { t } = this.props;
		documentsService.deleteStoreFolder(folderId)
			.then((response) => {
				if (response.ok) {
					toastr.info(t('common.success'), t('documents.folder.deleted', { name: folderName }));
					this.fetchFolders(true);

					if (this.props.onRefresh) {
						this.props.onRefresh();
					}
				} else {
					let errorMessage = t('documents.folder.deleteError');

					if (response.errors && response.errors.length > 0) {
						errorMessage = response.errors[0].description || errorMessage;
					}

					toastr.error('Error', errorMessage);
				}
			})
			.catch((error) => {
				console.error('Error deleting folder:', error);
				toastr.error('Error', t('documents.folder.deleteError'));
			});
	};

	toggleMenu = (folderId, e) => {
		e.preventDefault();
		e.stopPropagation();
		this.setState(prevState => ({
			openMenuId: prevState.openMenuId === folderId ? null : folderId
		}));
	};

	closeMenu = () => {
		this.setState({ openMenuId: null });
	};

	handleMenuAction = (action, folder) => {
		this.closeMenu();
		if (action === 'edit') {
			this.handleEditFolder(folder);
		} else if (action === 'delete') {
			this.handleDeleteFolder(folder);
		}
	};

	openUploadModal = () => {
		const { currentFolder } = this.state;
		const { user } = this.props;

		this.props.showModal({
			open: true,
			title: this.props.t('documents.uploadDocuments'),
			style: { width: '700px' },
			content: <EmployeeDocumentsUploadModal
				handleClose={this.closeModal}
				folderId={currentFolder.id}
				employeeId={user.id}
				onUploadSuccess={this.handleUploadSuccess}
			/>,
			closeModal: this.closeModal
		}, 'edit');
	}

	closeModal = () => {
		this.props.hideModal();
	}

	handleUploadSuccess = () => {
		this.fetchAttachments(this.state.currentFolder.id);
		this.closeModal();
	}

	// Método auxiliar para hacer matching entre attachments y signaturit data
	getSignatureDataForAttachment = (attachment) => {
		const { signaturitData } = this.state;

		if (!signaturitData || signaturitData.length === 0) {
			return null;
		}

		// Extraer el UUID del attachment.url
		const attachmentUuid = attachment.url;

		// Buscar TODOS los matches por original_file_uuid o completed_file_uuid
		// (un documento puede tener múltiples procesos de firma)
		const matches = signaturitData.filter(sig =>
			sig.original_file_uuid === attachmentUuid ||
			sig.completed_file_uuid === attachmentUuid
		);

		if (matches.length === 0) {
			return null;
		}

		// Si hay múltiples matches, retornar el más reciente por fecha de creación
		if (matches.length > 1) {
			matches.sort((a, b) => {
				const dateA = new Date(a.created);
				const dateB = new Date(b.created);
				return dateB - dateA; // Orden descendente (más reciente primero)
			});
		}

		return matches[0];
	}

	handleDataTableSort = ({ key, direction }) => {
		this.setState({ sortField: key, sortDirection: direction }, () => {
			if (this.state.attachments.length > 0) {
				const sorted = this.sortAttachments(this.state.attachments);
				this.setState({ attachments: sorted });
			}
		});
	};

	getAttachmentDataTableColumns = (isPendingList, user) => {
		const { t } = this.props;
		const cols = [
			{
				key: "name",
				header: t("documents.columns.document"),
				sortable: true,
				sortAccessor: (row) => (row.name || "").toLowerCase(),
				render: (row) => (
					<button
						type="button"
						className="btn-transparent btn-editable document-name"
						onClick={() => this.handleDownload(row)}
					>
						{row.name}
					</button>
				),
			},
		];

		if (isPendingList) {
			cols.push({
				key: "folder",
				header: t("documents.columns.folder"),
				sortable: true,
				sortAccessor: (row) =>
					(this.getFolderDisplayName(row) || "").toLowerCase(),
				render: (row) => this.getFolderDisplayName(row),
			});
		}

		cols.push(
			{
				key: "size",
				header: t("documents.columns.size"),
				sortable: false,
				render: (row) =>
					`${(row.size / 1024).toFixed(2).replace(".", ",")} Kb`,
			},
			{
				key: "created",
				header: t("documents.columns.created"),
				sortable: true,
				sortAccessor: (row) => new Date(row.created).getTime(),
				render: (row) =>
					new Date(row.created)
						.toLocaleString("es-ES", {
							day: "2-digit",
							month: "2-digit",
							year: "numeric",
							hour: "2-digit",
							minute: "2-digit",
						})
						.replace(",", " "),
			},
			{
				key: "author",
				header: t("documents.columns.author"),
				sortable: false,
				render: (row) =>
					row.creator
						? `${row.creator.name} ${row.creator.surname}`
						: "",
			}
		);

		if (!isPendingList) {
			cols.push({
				key: "signature",
				header: t("documents.columns.status"),
				sortable: false,
				render: (row) => {
					const isUserAdmin =
						user && user.roles && user.roles.includes("ADMIN");
					const signatureData = this.getSignatureDataForAttachment(row);
					const canSign =
						isSignableDocument(row.name) && isUserAdmin;
					const hasSignDocumentsSubscription =
						hasSubscriptionFeature("SIGN_DOCUMENTS");
					return (
						<SignatureStatus
							signatureData={signatureData}
							onStartSign={() => this.handleSign(row)}
							canSign={canSign}
							hasSubscriptionAccess={hasSignDocumentsSubscription}
						/>
					);
				},
			});
		}

		return cols;
	};

	getContent = (category) => {
		const { attachments, currentFolder, loading, error } = this.state;
		const { user } = this.props;
		const isPendingList = currentFolder?.isPendingList;

		if (loading) {
			return <Loading />;
		}

		const { t } = this.props;

		if (error) {
			return (
				<div className="alert alert-warning">
					<strong>{t("common.error")}:</strong> {error}
					<br />
					<small>{t("documents.refreshOrBack")}</small>
					<br />
					<button
						className="btn btn-outline-primary mt-2"
						onClick={() => this.props.history.push('/documents')}
					>
						{t("documents.backToDocuments")}
					</button>
				</div>
			);
		}

		if (!currentFolder) {
			return (
				<div className="alert alert-info">
					<strong>{t("common.error")}:</strong> {t("documents.loadingFolderInfo")}
					<br />
					<button
						className="btn btn-outline-primary mt-2"
						onClick={() => this.props.history.push('/documents')}
					>
						{t("documents.backToDocuments")}
					</button>
				</div>
			);
		}

		return (
			<>
				<div className="heading">
					<div className="heading-left">
						<h1 className="title">
							<FiArrowLeft
								className="button-icon"
								onClick={() => this.props.history.push('/documents')}
								title={t("documents.backToDocuments")}
							/>
							{" "}
							{currentFolder.name || `Carpeta ${currentFolder.id}`}
						</h1>
					</div>
					{!isPendingList && (
						<div className="heading-actions">
							<button
								type="button"
								title={t("documents.addDocuments")}
								onClick={this.openUploadModal}
								className="btn btn-new"
							>
								<FiPlus />
							</button>
						</div>
					)}
				</div>
				<div className="documents-folder-datatable-wrap w-100">
					<DataTable
						tableClassName="table-documents table-hover"
						columns={this.getAttachmentDataTableColumns(isPendingList, user)}
						rows={attachments || []}
						rowKey={(row) => row.url}
						sortState={{
							key: this.state.sortField,
							direction: this.state.sortDirection,
						}}
						onSortChange={this.handleDataTableSort}
						actionsColumn={{
							header: "Acciones",
							headerClassName: "text-end",
							className: "td-actions",
							render: (row) => this.getActions(row),
						}}
						emptyMessage={
							isPendingList ? (
								t("documents.noPendingSignatures")
							) : (
								<>
									{t("documents.noDocumentsInFolder")}
									<br />
									<small>{t("documents.useAddButton")}</small>
								</>
							)
						}
					/>
				</div>
			</>
		);
	};

	getActions = (attachment) => {
		const isPendingList = this.state.currentFolder?.isPendingList;

		return (
			<DocumentAttachmentActionsMenu
				attachment={attachment}
				fallbackStoreFolderId={isPendingList ? undefined : this.state.currentFolder?.id}
				foldersList={isPendingList ? this.state.folders : undefined}
				debugContext={isPendingList ? "pending-list/actions-menu" : undefined}
				onAfterMutation={() => {
					if (isPendingList) {
						this.fetchPendingSignatures();
					} else if (this.state.currentFolder?.id) {
						this.fetchAttachments(this.state.currentFolder.id);
					}
				}}
			/>
		);
	};

	handleDownload = async (attachment) => {
		const isPendingList = this.state.currentFolder?.isPendingList;
		if (isPendingList) {
			await downloadStoreFolderAttachment(
				attachment,
				undefined,
				this.state.folders,
				this.props.t
			);
			return;
		}

		const folderId = this.getStoreFolderIdForAttachment(attachment);
		if (!folderId) {
			toastr.error('Error', this.props.t('documents.errorDeterminingFolder'));
			return;
		}
		const requestOptions = {
			method: 'GET',
			headers: {
				'Authorization': `Bearer ${authService.token.access_token}`
			}
		};

		try {
			const response = await fetch(
				`${global.BASE_API}store-folders/${folderId}/attachments/${attachment.url}`,
				requestOptions
			);

			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			const blob = await response.blob();

			const url = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.download = attachment.name || 'documento';
			document.body.appendChild(link);
			link.click();

			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);

		} catch (error) {
			console.error('Error al descargar el archivo:', error);
			toastr.error('Error', this.props.t('documents.errorDownload'));
		}
	};

	handleDelete = (attachment) => {
		const { name, url } = attachment;
		const { t } = this.props;
		const folderId = this.getStoreFolderIdForAttachment(attachment);
		if (!folderId) {
			toastr.error('Error', t('documents.errorDeleteMissingFolder'));
			return;
		}
		this.props.showModal(
			{
				open: true,
				title: t('documents.deleteDocument'),
				message: t('documents.deleteDocumentConfirm', { name }),
				deleteAction: () => {
					this.props.hideModal();
					this.deleteAttachment(folderId, url, name);
				},
				style: {
					width: '400px',
				},
				closeModal: this.props.hideModal
			},
			'delete'
		);
	};

	handleSign = (attachment) => {
		const { t } = this.props;
		if (!isSignableDocument(attachment.name)) {
			toastr.error(t('documents.incompatibleFormat'), getFormatErrorMessage(attachment.name));
			return;
		}

		if (!hasSubscriptionFeature('SIGN_DOCUMENTS')) {
			this.setState({
				showUpgradeModal: true,
				upgradeFeature: t('documents.signWithSignaturit')
			});
			return;
		}

		const storeFolderId = this.getStoreFolderIdForAttachment(attachment);
		if (!storeFolderId) {
			toastr.error('Error', t('documents.errorDeterminingFolder'));
			return;
		}

		this.props.showModal({
			open: true,
			title: this.props.t('documents.configureSigners'),
			style: { width: '800px' },
			content: <DocumentSignContent
				closeModal={this.closeModal}
				attachment={attachment}
				storeFolderId={storeFolderId}
				currentUser={this.props.user}
				onSignSuccess={this.handleSignSuccess}
			/>,
			closeModal: this.closeModal
		}, 'edit');
	};

	handleSignSuccess = () => {
		if (this.state.currentFolder?.isPendingList) {
			this.fetchPendingSignatures();
		} else if (this.state.currentFolder?.id) {
			this.fetchAttachments(this.state.currentFolder.id);
		}
	};

	handleMove = (attachment) => {
		const { t } = this.props;
		const currentFolderId = this.getStoreFolderIdForAttachment(attachment);
		if (!currentFolderId) {
			toastr.error('Error', t('documents.errorMoveMissingFolder'));
			return;
		}

		this.props.showModal({
			open: true,
			title: t('documents.moveDocument'),
			style: { width: '700px' },
			content: <DocumentMoveModal
				handleClose={this.closeModal}
				attachment={attachment}
				currentFolderId={currentFolderId}
				onMoveSuccess={this.handleMoveSuccess}
			/>,
			closeModal: this.closeModal
		}, 'edit');
	};

	handleMoveSuccess = () => {
		if (this.state.currentFolder?.isPendingList) {
			this.fetchPendingSignatures();
		} else if (this.state.currentFolder?.id) {
			this.fetchAttachments(this.state.currentFolder.id);
		}
	};

	handleRename = (attachment) => {
		const { t } = this.props;
		const currentFolderId = this.getStoreFolderIdForAttachment(attachment);
		if (!currentFolderId) {
			toastr.error('Error', t('documents.errorRenameMissingFolder'));
			return;
		}

		this.props.showModal({
			open: true,
			title: t('documents.renameDocumentTitle'),
			style: { width: '500px' },
			content: <DocumentRenameModal
				handleClose={this.closeModal}
				attachment={attachment}
				currentFolderId={currentFolderId}
				onRenameSuccess={this.handleRenameSuccess}
			/>,
			closeModal: this.closeModal
		}, 'edit');
	};

	handleRenameSuccess = () => {
		if (this.state.currentFolder?.isPendingList) {
			this.fetchPendingSignatures();
		} else if (this.state.currentFolder?.id) {
			this.fetchAttachments(this.state.currentFolder.id);
		}
	};

	deleteAttachment = (folderId, url, name) => {
		const { t } = this.props;
		const wasPendingList = this.state.currentFolder?.isPendingList;
		documentsService.deleteAttachment(folderId, url)
			.then((response) => {
				if (!response?.ok) {
					const backendError =
						response?.errors?.[0]?.description ||
						response?.message ||
						t("documents.errorDeleteDocument");
					throw new Error(backendError);
				}

				this.setState(prev => ({
					attachments: prev.attachments.filter(a => a.url !== url)
				}));
				toastr.info(t('common.success'), t('documents.fileDeleted', { name }));

				if (wasPendingList) {
					this.fetchPendingSignatures();
				} else {
					this.fetchFolders(true);
				}
			})
			.catch((error) => {
				toastr.error('Error', error?.message || t('documents.errorDeleteDocument'));
			});
	};

	render() {
		const cat = this.props.category;

		return (
			<>
				{cat !== undefined ? (
					this.getContent(cat)
				) : (
					<DocumentsNotificationsProvider>
						<PendingSignatureCards />
						{this.getCategories()}
						<RecentDocumentsList folders={this.state.folders} />
					</DocumentsNotificationsProvider>
				)}
				<UpgradeModal
					show={this.state.showUpgradeModal}
					onClose={() => this.setState({ showUpgradeModal: false })}
					feature={this.state.upgradeFeature}
					requiredPlan="Pro"
				/>
			</>
		);
	}
}

const mapStateToProps = (reducers) => {
	return {
		user: reducers.authReducer.user,
	};
};

const mapDispatchToProps = (dispatch) => ({
	showModal: (modalProps, modalType) => dispatch(showModal({ modalProps, modalType })),
	hideModal: () => dispatch(hideModal()),
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(withTranslation()(MyDocuments)));
